import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { username, password });
      localStorage.setItem("juke_admin_token", data.access_token);
      toast.success("Welcome back.");
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#050505] flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(197,160,89,0.15), transparent 50%), radial-gradient(circle at 80% 80%, rgba(197,160,89,0.08), transparent 50%)'
      }} />

      <a href="/" data-testid="admin-back-home" className="absolute top-8 left-8 flex items-center gap-2 text-sm text-zinc-500 hover:text-[#C5A059] transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </a>

      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="relative w-full max-w-md bg-[#0d0d0d] border border-white/10 rounded-2xl p-10 shadow-[0_8px_48px_rgba(0,0,0,0.6)]"
      >
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-full border border-[#C5A059]/30 bg-[#C5A059]/5 flex items-center justify-center mb-5">
            <Lock className="w-5 h-5 text-[#C5A059]" />
          </div>
          <h1 className="font-display text-3xl text-white font-light">Admin Access</h1>
          <p className="text-sm text-zinc-500 mt-2">Juke Transfers — control room</p>
        </div>

        <div className="space-y-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="username" className="text-xs uppercase tracking-wider text-zinc-400">Username</Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="bg-[#0a0a0a] border-white/10 text-zinc-100 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 h-12"
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-400">Password</Label>
            <Input
              id="password"
              type="password"
              data-testid="admin-password-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0a0a0a] border-white/10 text-zinc-100 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 h-12"
              required
            />
          </div>
        </div>

        <button
          type="submit"
          data-testid="admin-login-submit"
          disabled={loading}
          className="mt-8 w-full h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black font-semibold transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}
